import React, { useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ChatProvider } from '@/contexts/ChatContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { SiteModeProvider } from '@/contexts/SiteModeContext';
import { badgeService } from '@/shared/api/badges';
import queryClient from '@/shared/api/queryClient';
import { useSSE } from '@/shared/hooks/useSSE';

/**
 * Initializes badge service when user is authenticated.
 * Replaces the deprecated BadgeCountProvider.
 */
function BadgeServiceInit(): any {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      badgeService.init({ isAdmin: user.role === 'ADMIN' });
    }
    return () => {
      if (!user) {
        badgeService.destroy();
      }
    };
  }, [user]);

  return null;
}

/**
 * Opens SSE connection when user is authenticated.
 * Events are routed to React Query invalidations via useSSE.
 */
function SSEConnector(): any {
  const { user } = useAuth();
  useSSE(!!user);
  return null;
}

export default function AppProviders({ children }: any) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BadgeServiceInit />
        <SSEConnector />
        <NotificationProvider>
          <SiteModeProvider>
            <ChatProvider>{children}</ChatProvider>
          </SiteModeProvider>
        </NotificationProvider>
      </AuthProvider>
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
